import React from 'react';
import ReactTimeout from 'react-timeout';
import differenceBy from 'lodash/differenceBy';

import AsyncSelect from 'react-select/async';
import { components } from 'react-select';

import Field from 'components/lib/FormFields/components/Field';
import Control from 'components/lib/FormFields/components/Control';
import Icon from 'components/lib/Icon';
import Text from 'components/lib/Text';

import { request } from 'core/constants';

import Modal from 'components/lib/Modal';
import AddNewClient from 'features/AddNewClient';

import AddButton from './components/AddButton';
import MultiValueLabel from './components/MultiValueLabel';
import MultiValueRemove from './components/MultiValueRemove';

import AwesomeDebouncePromise from 'awesome-debounce-promise';

class ClientSelection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      inputValue: '',
      options: (this.props.defaultValue || {}).clients || [],
      isWorking: false,
      noOptions: false,
      modalIsOpen: false,
    };
    this.asyncRef = React.createRef();
    this.proposalId = this.props.proposalId ?? 0;
    this.proposalOnly = this.props.proposalOnly ?? false;
    this.debouncePromiseOptions = AwesomeDebouncePromise(
      this.promiseOptions.bind(this),
      400
    );
  }

  componentDidMount() {
    const { defaultValue = {} } = this.props;

    if ((defaultValue.clients || []).length > 0) {
      const defaultOptions = (this.props.defaultValue || {}).clients || [];
      this.props.handleFieldChanges(
        'clients',
        [...defaultOptions].map((item) => ({
          id: item.id,
          main: item.id === this.state.mainClientId,
        })),
        {
          isValid: defaultOptions.length > 0,
          value: [...defaultOptions].map((item) => ({
            id: item.id,
            main: item.id === this.state.mainClientId,
          })),
        }
      );
    }
  }

  onAdded(result) {
    if (result.isSuccess) {
      this.setState({
        modalIsOpen: false,
        options: [
          ...this.state.options,
          { id: result.data.id, name: result.data.name, email: result.data.email },
        ],
      });

      this.setOptions(this.state.options);
    } else {
      this.props.notification({
        message: 'Não foi possível adicionar esse Cliente',
        status: 'danger',
      });
    }
  }

  promiseOptions(inputValue) {
    const { clients = [] } = this.props.defaultValue || {};

    const items = clients.filter((client) =>
      client.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    if (items.length > 0) {
      return items;
    }

    let url = `{company}/client/search?q=${inputValue}`;
    if (this.proposalOnly && this.proposalId > 0) {
      url = `${url}&relations[proposals][id]=${this.proposalId}`;
    }

    return request.get(url).then((res) => res.data);
  }

  setOptions(options) {
    this.setState({ options, inputValue: '' });
    if (this.props.onSelect) this.props.onSelect(options);
    this.props.handleFieldChanges(
      'clients',
      [...options].map((item) => ({
        id: item.id,
        main: item.id === this.state.mainClientId,
      })),
      {
        isValid: options.length > 0,
        value: [...options].map((item) => ({
          id: item.id,
          main: item.id === this.state.mainClientId,
        })),
      }
    );
  }

  importClient(options, index) {
    request
      .get(
        '{company}/client/import/' +
          options[index].source +
          '?id=' +
          options[index].id
      )
      .then((response) => {
        options[index].id = response.data.id;
        options[index].source = response.data.source;
        options[index].name = response.data.name;
        options[index].email = response.data.email;
        this.setOptions(options);
      });
  }

  onChange(options) {
    // Check if removed items can be removed
    const { clients, canRemove = true } = this.props;
    if (!canRemove) {
      return;
    }
    const items = clients || this.state.options;
    if (
      options.length < items.length &&
      !!items.find((option) => option.forbiddenRemove)
    ) {
      const mustRemainItems = differenceBy(items, options, 'id').filter(
        (option) => option.forbiddenRemove
      );
      options = [...options, ...mustRemainItems];
      return;
    }
    for (let index in options) {
      if (options[index].source && options[index].source !== 'izee') {
        options[index].name = 'Importando...';
        this.importClient(options, index);
      }
    }
    this.setOptions(options);
  }

  render() {
    const {
      showAddition,
      disabled = false,
      isMultiStep,
      clients,
      isValid,
    } = this.props;

    const DropdownIndicator = (props) => (
      <components.DropdownIndicator {...props}>
        <Icon name="fas fa-user" />
      </components.DropdownIndicator>
    );

    const Option = (props) => {
      const data = props.data || {};
      return (
        <components.Option {...props}>
          {data.cnpj ? (
            <div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#4a4a4a' }}>
                {data.corporate_name || 'Sem nome'}
              </span>
              <br />
              <span style={{ fontSize: 12 }}>{data.cnpj}</span>
            </div>
          ) : (
            <div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#4a4a4a' }}>
                {data.name || 'Sem nome'}
              </span>
              <br />
              {data.withSpouse && (
                <>
                  <br />
                  <span style={{ fontSize: 12 }}>Cônjuge: {data.spouse_name}</span>
                </>
              )}
              <span style={{ fontSize: 12 }}>{data.email}</span>
            </div>
          )}
        </components.Option>
      );
    };

    const LoadingIndicator = (props) => null;

    return !isMultiStep ? (
      <React.Fragment>
        <Field label="Clientes">
          <Control>
            <div className="react-select-wrapper" style={{ display: 'flex' }}>
              <AsyncSelect
                styles={
                  !isValid && {
                    control: (provided) => ({
                      ...provided,
                      borderColor: '#ff0000',
                    }),
                  }
                }
                defaultOptions
                ref={(ref) => (this.asyncRef = ref)}
                classNamePrefix="react-select"
                defaultValue={[]}
                name="client"
                id="client-selection"
                isMulti
                isDisabled={disabled || this.state.isWorking}
                isLoading={this.state.isWorking}
                value={clients}
                loadingMessage={() => 'Pesquisando por clientes...'}
                noOptionsMessage={() => <span>Nenhum cliente encontrado</span>}
                placeholder="Selecionar clientes"
                menuPosition="fixed"
                maxMenuHeight={162}
                minMenuHeight={100}
                onInputChange={this.handleInputChange.bind(this)}
                inputValue={this.state.inputValue}
                onChange={this.onChange.bind(this)}
                isClearable={false}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                components={{
                  MultiValueLabel,
                  MultiValueRemove,
                  DropdownIndicator,
                  LoadingIndicator,
                  Option,
                }}
                loadOptions={this.debouncePromiseOptions.bind(this)}
              />
              {showAddition ? (
                <AddButton onClick={() => this.setState({ modalIsOpen: true })} />
              ) : null}
            </div>
          </Control>
          {!isValid && <p className="help is-danger">{this.props.errorMessage}</p>}
        </Field>
        <Modal
          title="Criar novo(a) cliente"
          isOpen={this.state.modalIsOpen}
          onClose={() => {
            this.setState({ modalIsOpen: false });
          }}
          backgroundContentStyle={{
            display: 'block',
          }}
        >
          <AddNewClient onSuccess={this.onAdded.bind(this)} />
        </Modal>
      </React.Fragment>
    ) : (
      <React.Fragment>
        <Field label="Clientes">
          <Control>
            <div className="react-select-wrapper">
              <AsyncSelect
                defaultOptions
                ref={(ref) => (this.asyncRef = ref)}
                classNamePrefix="react-select"
                defaultValue={[]}
                name="client"
                isMulti
                isDisabled={this.state.isWorking || disabled}
                isLoading={this.state.isWorking}
                value={clients}
                loadingMessage={() => 'Pesquisando por clientes...'}
                noOptionsMessage={() => <span>Nenhum cliente encontrado</span>}
                placeholder="Selecionar clientes"
                menuPosition="fixed"
                maxMenuHeight={162}
                minMenuHeight={100}
                onInputChange={this.handleInputChange.bind(this)}
                inputValue={this.state.inputValue}
                onChange={this.onChange.bind(this)}
                isClearable={false}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                components={{
                  MultiValueLabel,
                  MultiValueRemove,
                  DropdownIndicator,
                  Option,
                }}
                loadOptions={this.debouncePromiseOptions.bind(this)}
              />
            </div>
          </Control>
        </Field>
      </React.Fragment>
    );
  }

  handleInputChange(inputValue) {
    this.setState({ inputValue });
    return inputValue;
  }
}

ClientSelection.propTypes = {};

export default ReactTimeout(ClientSelection);
